/*
 * Description:
 * ---------------------
 * Activity & Events Timeline — aggregated, fleet-wide event log.
 *
 * Polls every registered edge node's /diagnostics endpoint, extracts
 * recent_events, merges them into a single chronological timeline,
 * and displays an interactive, filterable feed.
 *
 * Admins can filter by severity, node, source, and free-text search.
 * Events are grouped by time buckets (Today / Yesterday / Earlier)
 * and displayed with a vertical timeline rail for visual scanning.
 *
 * Also synthesises connectivity events (node online/offline) by
 * comparing node.stale across polling cycles.
 *
 * ! file components/EventLogPanel.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2025
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiAlertTriangle,
  FiAlertCircle,
  FiInfo,
  FiCheckCircle,
  FiSearch,
  FiFilter,
  FiX,
  FiRefreshCw,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiActivity,
  FiZap,
  FiWifi,
} from "react-icons/fi";
import { ago } from "../utils/formatters";
import { fetchWithTimeout, isAbortError } from "../utils/fetchWithTimeout";

// ── Constants ────────────────────────────────────────────────────────────────

const SEVERITY_META = {
  error:   { icon: FiAlertCircle,    color: "#EF4444", bg: "rgba(239,68,68,0.08)",   label: "Error" },
  warn:    { icon: FiAlertTriangle,  color: "#F59E0B", bg: "rgba(245,158,11,0.08)",  label: "Warning" },
  info:    { icon: FiInfo,           color: "#6366F1", bg: "rgba(99,102,241,0.08)",   label: "Info" },
  success: { icon: FiCheckCircle,    color: "#22C55E", bg: "rgba(34,197,94,0.08)",    label: "Success" },
  system:  { icon: FiActivity,       color: "#22D3EE", bg: "rgba(34,211,238,0.08)",   label: "System" },
};

const getSevMeta = (level) => SEVERITY_META[level] || SEVERITY_META.info;

const PAGE_SIZE = 50;

// ── Time bucket helpers ──────────────────────────────────────────────────────

const startOfDay = (d) => {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
};

const getTimeBucket = (ts) => {
  const now = new Date();
  const today = startOfDay(now);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const sevenDays = new Date(today);
  sevenDays.setDate(sevenDays.getDate() - 7);

  const d = new Date(ts);
  if (d >= today) return "Today";
  if (d >= yesterday) return "Yesterday";
  if (d >= sevenDays) return "This Week";
  return "Earlier";
};

// ── Unique sources from events ───────────────────────────────────────────────

const extractSources = (events) => {
  const set = new Set();
  events.forEach((e) => { if (e.source) set.add(e.source); });
  return Array.from(set).sort();
};

// ── Severity stat counter ────────────────────────────────────────────────────

function SeverityCounter({ events }) {
  const counts = useMemo(() => {
    const c = { error: 0, warn: 0, info: 0, success: 0, system: 0 };
    events.forEach((e) => {
      const lev = (e.level || "info").toLowerCase();
      if (c[lev] !== undefined) c[lev]++;
      else c.info++;
    });
    return c;
  }, [events]);

  return (
    <div className="evlog-severity-bar">
      {Object.entries(SEVERITY_META).map(([key, meta]) => (
        <div key={key} className="evlog-sev-chip" style={{ "--sev-color": meta.color, "--sev-bg": meta.bg }}>
          <meta.icon size={13} />
          <span className="evlog-sev-count">{counts[key]}</span>
          <span className="evlog-sev-label">{meta.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── Single event row ─────────────────────────────────────────────────────────

function EventRow({ event, expanded, onToggle }) {
  const meta = getSevMeta((event.level || "info").toLowerCase());
  const Icon = meta.icon;
  const ts = event.time ? new Date(event.time) : null;
  const timeStr = ts ? ts.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "";
  const dateStr = ts ? ts.toLocaleDateString([], { month: "short", day: "numeric" }) : "";

  return (
    <div className={`evlog-row ${expanded ? "expanded" : ""}`}>
      {/* Timeline dot */}
      <div className="evlog-rail">
        <div className="evlog-dot" style={{ background: meta.color, boxShadow: `0 0 8px ${meta.color}40` }} />
        <div className="evlog-line" />
      </div>

      {/* Content */}
      <div className="evlog-content" onClick={onToggle} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); } }}>
        <div className="evlog-row-header">
          <div className="evlog-row-left">
            <span className="evlog-row-icon" style={{ color: meta.color }}>
              <Icon size={15} />
            </span>
            <span className="evlog-row-msg">{event.message || "—"}</span>
          </div>
          <div className="evlog-row-right">
            <span className="evlog-row-time" title={ts ? ts.toISOString() : ""}>
              {timeStr}
            </span>
            {expanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
          </div>
        </div>

        {/* Tags */}
        <div className="evlog-row-tags">
          {event.nodeId && (
            <span className="evlog-tag evlog-tag-node">{event.nodeId}</span>
          )}
          {event.source && (
            <span className="evlog-tag evlog-tag-source">{event.source}</span>
          )}
          <span className="evlog-tag evlog-tag-sev" style={{ color: meta.color, background: meta.bg }}>
            {meta.label}
          </span>
          {dateStr && (
            <span className="evlog-tag evlog-tag-date">{dateStr}</span>
          )}
        </div>

        {/* Expanded detail */}
        {expanded && (
          <div className="evlog-detail">
            <div className="evlog-detail-grid">
              <div className="evlog-detail-item">
                <span className="evlog-detail-label">Full Timestamp</span>
                <span className="evlog-detail-value mono">{ts ? ts.toISOString() : "—"}</span>
              </div>
              <div className="evlog-detail-item">
                <span className="evlog-detail-label">Edge Node</span>
                <span className="evlog-detail-value">{event.nodeId || "—"}</span>
              </div>
              <div className="evlog-detail-item">
                <span className="evlog-detail-label">Source</span>
                <span className="evlog-detail-value">{event.source || "—"}</span>
              </div>
              <div className="evlog-detail-item">
                <span className="evlog-detail-label">Severity</span>
                <span className="evlog-detail-value" style={{ color: meta.color }}>{meta.label}</span>
              </div>
            </div>
            <div className="evlog-detail-msg">
              <span className="evlog-detail-label">Message</span>
              <p className="evlog-detail-full">{event.message}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Time bucket header ───────────────────────────────────────────────────────

function BucketHeader({ label, count }) {
  return (
    <div className="evlog-bucket">
      <FiClock size={13} className="evlog-bucket-icon" />
      <span className="evlog-bucket-label">{label}</span>
      <span className="evlog-bucket-count">{count} events</span>
      <div className="evlog-bucket-line" />
    </div>
  );
}

// ── Main Panel ───────────────────────────────────────────────────────────────

export default function EventLogPanel({ nodes, proxyBase }) {
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastFetch, setLastFetch] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [severityFilter, setSeverityFilter] = useState("all"); // "all" | "error" | "warn" | "info" | "success" | "system"
  const [nodeFilter, setNodeFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [paused, setPaused] = useState(false);
  const prevNodeStates = useRef({}); // track stale transitions
  const seenKeys = useRef(new Set()); // deduplicate across polls
  const feedRef = useRef(null);

  // controller scoped to the most recent in-flight fetchEvents() so a tab
  // switch / unmount cancels the multi-node Promise.all instead of
  // letting late responses land on unmounted state.
  const fetchControllerRef = useRef(null);

  // ── Poll all nodes' diagnostics ─────────────────────────────────────────
  const fetchEvents = useCallback(async () => {
    if (!proxyBase || nodes.length === 0) {
      setLoading(false);
      return;
    }

    // Cancel any still-pending fan-out from the previous tick. With ~10
    // nodes at 8s cadence, slow / dead nodes used to stack up indefinitely.
    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
    }
    const controller = new AbortController();
    fetchControllerRef.current = controller;

    const fetches = nodes.map(async (node) => {
      try {
        const url = `${proxyBase}/node/${encodeURIComponent(node.node_id)}/diagnostics`;
        const res = await fetchWithTimeout(url, { signal: controller.signal });
        if (!res.ok) return [];
        const data = await res.json();

        const events = (data.recent_events || []).map((ev) => ({
          ...ev,
          nodeId: node.node_id,
          _key: `${node.node_id}:${ev.time}:${ev.source}:${(ev.message || "").slice(0, 40)}`,
        }));
        return events;
      } catch (err) {
        if (isAbortError(err)) return [];
        return [];
      }
    });

    try {
      const results = await Promise.all(fetches);
      const incoming = results.flat();

      // Synthesise connectivity events from stale transitions
      const synthEvents = [];
      nodes.forEach((n) => {
        const wasStale = prevNodeStates.current[n.node_id];
        const isStale = n.stale === true;
        if (wasStale === undefined) {
          // first observation — no event
        } else if (wasStale && !isStale) {
          synthEvents.push({
            time: new Date().toISOString(),
            level: "success",
            source: "fleet",
            message: `Node "${n.node_id}" came online`,
            nodeId: n.node_id,
            _key: `synth:online:${n.node_id}:${Date.now()}`,
          });
        } else if (!wasStale && isStale) {
          synthEvents.push({
            time: new Date().toISOString(),
            level: "warn",
            source: "fleet",
            message: `Node "${n.node_id}" went offline (stale)`,
            nodeId: n.node_id,
            _key: `synth:offline:${n.node_id}:${Date.now()}`,
          });
        }
        prevNodeStates.current[n.node_id] = isStale;
      });

      // Merge new events only (dedup by _key)
      setAllEvents((prev) => {
        const newItems = [...incoming, ...synthEvents].filter((ev) => !seenKeys.current.has(ev._key));
        newItems.forEach((ev) => seenKeys.current.add(ev._key));

        if (newItems.length === 0) return prev;

        // Merge and sort descending — most recent first
        const merged = [...newItems, ...prev]
          .sort((a, b) => {
            const tA = a.time ? new Date(a.time).getTime() : 0;
            const tB = b.time ? new Date(b.time).getTime() : 0;
            return tB - tA;
          })
          .slice(0, 500); // cap at 500 events for performance
        return merged;
      });

      setError("");
      setLastFetch(new Date());
    } catch (err) {
      setError(err.message || "Failed to fetch events");
    } finally {
      setLoading(false);
    }
  }, [nodes, proxyBase]);

  // ── Polling loop ────────────────────────────────────────────────────────
  useEffect(() => {
    if (paused) return;
    let active = true;

    const poll = () => { if (active) fetchEvents(); };
    setLoading(true);
    poll();
    const timer = setInterval(poll, 8000);

    // Pause on visibility change
    const onVisibility = () => {
      if (document.hidden) clearInterval(timer);
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      active = false;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      // Cancel any still-running fan-out so late responses don't write to
      // unmounted state (and stop pinning sockets to dead edges).
      if (fetchControllerRef.current) {
        fetchControllerRef.current.abort();
      }
    };
  }, [fetchEvents, paused]);

  // ── Derived data ────────────────────────────────────────────────────────
  const allSources = useMemo(() => extractSources(allEvents), [allEvents]);
  const nodeIds = useMemo(() => [...new Set(allEvents.map((e) => e.nodeId).filter(Boolean))].sort(), [allEvents]);

  const filtered = useMemo(() => {
    let result = allEvents;

    if (severityFilter !== "all") {
      result = result.filter((e) => (e.level || "info").toLowerCase() === severityFilter);
    }
    if (nodeFilter !== "all") {
      result = result.filter((e) => e.nodeId === nodeFilter);
    }
    if (sourceFilter !== "all") {
      result = result.filter((e) => e.source === sourceFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (e) =>
          (e.message || "").toLowerCase().includes(q) ||
          (e.source || "").toLowerCase().includes(q) ||
          (e.nodeId || "").toLowerCase().includes(q)
      );
    }

    return result;
  }, [allEvents, severityFilter, nodeFilter, sourceFilter, searchQuery]);

  const paged = useMemo(() => filtered.slice(0, pageSize), [filtered, pageSize]);
  const hasMore = filtered.length > pageSize;

  // Group into time buckets
  const buckets = useMemo(() => {
    const map = new Map();
    paged.forEach((ev) => {
      const bucket = ev.time ? getTimeBucket(ev.time) : "Earlier";
      if (!map.has(bucket)) map.set(bucket, []);
      map.get(bucket).push(ev);
    });
    return map;
  }, [paged]);

  const activeFilterCount =
    (severityFilter !== "all" ? 1 : 0) +
    (nodeFilter !== "all" ? 1 : 0) +
    (sourceFilter !== "all" ? 1 : 0) +
    (searchQuery.trim() ? 1 : 0);

  const clearAllFilters = () => {
    setSeverityFilter("all");
    setNodeFilter("all");
    setSourceFilter("all");
    setSearchQuery("");
  };

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="evlog-panel">
      {/* ── Header row ── */}
      <div className="evlog-header">
        <div className="evlog-header-left">
          <h2 className="evlog-title">
            <FiActivity size={20} className="evlog-title-icon" />
            Activity Timeline
          </h2>
          <span className="evlog-subtitle">
            {allEvents.length} events across {nodeIds.length} node{nodeIds.length !== 1 ? "s" : ""}
            {lastFetch && (
              <> · Updated {lastFetch.toLocaleTimeString()}</>
            )}
          </span>
        </div>
        <div className="evlog-header-right">
          <button
            className={`evlog-pause-btn ${paused ? "paused" : ""}`}
            onClick={() => setPaused((v) => !v)}
            title={paused ? "Resume live updates" : "Pause live updates"}
          >
            {paused ? (
              <><FiZap size={14} /> Resume</>
            ) : (
              <><FiRefreshCw size={14} className={loading ? "evlog-spin" : ""} /> Live</>
            )}
          </button>
        </div>
      </div>

      {/* ── Severity summary bar ── */}
      <SeverityCounter events={allEvents} />

      {/* ── Filter toolbar ── */}
      <div className="evlog-toolbar">
        <div className="evlog-search-wrap">
          <FiSearch size={14} className="evlog-search-icon" />
          <input
            type="text"
            className="evlog-search-input"
            placeholder="Search events…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search events"
          />
          {searchQuery && (
            <button className="evlog-search-clear" onClick={() => setSearchQuery("")} aria-label="Clear search">
              <FiX size={12} />
            </button>
          )}
        </div>

        <div className="evlog-filter-group">
          <FiFilter size={13} style={{ opacity: 0.5 }} />

          {/* Severity filter */}
          <select
            className="evlog-filter-select"
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            aria-label="Filter by severity"
          >
            <option value="all">All Severities</option>
            {Object.entries(SEVERITY_META).map(([key, meta]) => (
              <option key={key} value={key}>{meta.label}</option>
            ))}
          </select>

          {/* Node filter */}
          <select
            className="evlog-filter-select"
            value={nodeFilter}
            onChange={(e) => setNodeFilter(e.target.value)}
            aria-label="Filter by node"
          >
            <option value="all">All Nodes</option>
            {nodeIds.map((id) => (
              <option key={id} value={id}>{id}</option>
            ))}
          </select>

          {/* Source filter */}
          <select
            className="evlog-filter-select"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            aria-label="Filter by source"
          >
            <option value="all">All Sources</option>
            {allSources.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        {activeFilterCount > 0 && (
          <button className="evlog-clear-filters" onClick={clearAllFilters}>
            <FiX size={12} />
            Clear {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""}
          </button>
        )}
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="evlog-error">
          <FiAlertCircle size={14} /> {error}
        </div>
      )}

      {/* ── Loading state ── */}
      {loading && allEvents.length === 0 && (
        <div className="evlog-loading">
          <div className="evlog-loading-spinner" />
          <span>Collecting events from {nodes.length} node{nodes.length !== 1 ? "s" : ""}…</span>
        </div>
      )}

      {/* ── Empty state ── */}
      {!loading && allEvents.length === 0 && nodes.length === 0 && (
        <div className="evlog-empty">
          <div className="evlog-empty-icon">
            <FiWifi size={40} />
          </div>
          <h3>No Edge Nodes</h3>
          <p>Register edge controllers to start seeing activity events.</p>
        </div>
      )}
      {!loading && allEvents.length === 0 && nodes.length > 0 && (
        <div className="evlog-empty">
          <div className="evlog-empty-icon">
            <FiClock size={40} />
          </div>
          <h3>No Events Yet</h3>
          <p>Events from your {nodes.length} edge node{nodes.length !== 1 ? "s" : ""} will appear here as they occur.</p>
        </div>
      )}

      {/* ── No results for filters ── */}
      {allEvents.length > 0 && filtered.length === 0 && (
        <div className="evlog-empty">
          <FiSearch size={32} style={{ opacity: 0.25, marginBottom: 8 }} />
          <h3>No Matching Events</h3>
          <p>Try broadening your filters.</p>
          <button className="evlog-clear-filters" onClick={clearAllFilters} style={{ marginTop: 8 }}>
            Clear all filters
          </button>
        </div>
      )}

      {/* ── Event feed ── */}
      {paged.length > 0 && (
        <div className="evlog-feed" ref={feedRef}>
          {Array.from(buckets.entries()).map(([bucket, events]) => (
            <div key={bucket} className="evlog-bucket-group">
              <BucketHeader label={bucket} count={events.length} />
              {events.map((ev) => (
                <EventRow
                  key={ev._key}
                  event={ev}
                  expanded={expandedId === ev._key}
                  onToggle={() => setExpandedId((prev) => (prev === ev._key ? null : ev._key))}
                />
              ))}
            </div>
          ))}

          {/* Load more */}
          {hasMore && (
            <div className="evlog-load-more">
              <button
                className="evlog-load-more-btn"
                onClick={() => setPageSize((prev) => prev + PAGE_SIZE)}
              >
                Show more ({filtered.length - pageSize} remaining)
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Footer ── */}
      {allEvents.length > 0 && (
        <div className="evlog-footer">
          <span className="evlog-footer-stat">
            Showing {Math.min(paged.length, filtered.length)} of {filtered.length} events
            {activeFilterCount > 0 && ` (${allEvents.length} total)`}
          </span>
          {paused && (
            <span className="evlog-footer-paused">
              <FiZap size={12} /> Updates paused
            </span>
          )}
        </div>
      )}
    </div>
  );
}
